import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ClientProfileModal = ({ isOpen, onClose, client }) => {
  const [activeTab, setActiveTab] = useState('profile');

  if (!isOpen || !client) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(new Date(date));
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'individual':
        return 'bg-blue-100 text-blue-800';
      case 'professional':
        return 'bg-green-100 text-green-800';
      case 'subcontractor':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const mockQuoteHistory = [
    {
      id: 'Q-2024-001',
      date: '2024-12-15T10:30:00',
      amount: 15750.00,
      status: 'approved',
      description: 'Cuisine moderne avec îlot central'
    },
    {
      id: 'Q-2024-002',
      date: '2024-11-28T14:15:00',
      amount: 8950.00,
      status: 'pending',
      description: 'Rénovation cuisine appartement'
    },
    {
      id: 'Q-2024-003',
      date: '2024-10-12T09:45:00',
      amount: 12300.00,
      status: 'rejected',
      description: 'Cuisine ouverte salon'
    }
  ];

  const mockInteractionHistory = [
    {
      id: 1,
      type: 'email',
      date: '2024-12-20T16:30:00',
      description: 'Devis Q-2024-001 approuvé par le client',
      icon: 'Mail'
    },
    {
      id: 2,
      type: 'call',
      date: '2024-12-18T11:15:00',
      description: 'Appel téléphonique - Discussion des finitions',
      icon: 'Phone'
    },
    {
      id: 3,
      type: 'meeting',
      date: '2024-12-15T14:00:00',
      description: 'Rendez-vous sur site pour prise de mesures',
      icon: 'Calendar'
    },
    {
      id: 4,
      type: 'quote',
      date: '2024-12-15T10:30:00',
      description: 'Génération du devis Q-2024-001',
      icon: 'FileText'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved':
        return 'Approuvé';
      case 'pending':
        return 'En attente';
      case 'rejected':
        return 'Rejeté';
      default:
        return 'Inconnu';
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profil', icon: 'User' },
    { id: 'quotes', label: 'Devis', icon: 'FileText' },
    { id: 'history', label: 'Historique', icon: 'Clock' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-surface border border-border rounded-lg shadow-soft-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-text-primary">
              {client?.firstName} {client?.lastName}
            </h2>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(client?.category)}`}>
              {client?.category === 'individual' ? 'Particulier' : 
               client?.category === 'professional' ? 'Professionnel' : 'Sous-traitant'}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
          />
        </div>

        <div className="flex border-b border-border">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-smooth ${
                activeTab === tab?.id
                  ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-text-primary">Informations personnelles</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Icon name="User" size={16} className="text-text-secondary" />
                      <span className="text-text-primary">{client?.firstName} {client?.lastName}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="Mail" size={16} className="text-text-secondary" />
                      <span className="text-text-primary">{client?.email}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="Phone" size={16} className="text-text-secondary" />
                      <span className="text-text-primary">{client?.phone}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="MapPin" size={16} className="text-text-secondary" />
                      <span className="text-text-primary">{client?.address}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-text-primary">Statistiques</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-text-primary">{formatCurrency(client?.totalValue)}</div>
                      <div className="text-sm text-text-secondary">Chiffre d'affaires</div>
                    </div>
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-text-primary">{client?.totalQuotes}</div>
                      <div className="text-sm text-text-secondary">Devis générés</div>
                    </div>
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-text-primary">{client?.activeProjects}</div>
                      <div className="text-sm text-text-secondary">Projets actifs</div>
                    </div>
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-text-primary">{formatDate(client?.lastInteraction)?.split(' ')?.[0]}</div>
                      <div className="text-sm text-text-secondary">Dernière interaction</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'quotes' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-text-primary">Historique des devis</h3>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                >
                  Nouveau devis
                </Button>
              </div>
              <div className="space-y-3">
                {mockQuoteHistory?.map((quote) => (
                  <div key={quote?.id} className="bg-muted/30 border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <span className="font-medium text-text-primary">{quote?.id}</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(quote?.status)}`}>
                          {getStatusLabel(quote?.status)}
                        </span>
                      </div>
                      <span className="text-lg font-semibold text-text-primary">{formatCurrency(quote?.amount)}</span>
                    </div>
                    <p className="text-sm text-text-secondary mb-2">{quote?.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-text-secondary">{formatDate(quote?.date)}</span>
                      <div className="flex items-center space-x-2">
                        <Button variant="ghost" size="sm" iconName="Eye" title="Voir le devis" />
                        <Button variant="ghost" size="sm" iconName="Download" title="Télécharger" />
                        <Button variant="ghost" size="sm" iconName="Mail" title="Envoyer par email" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Historique des interactions</h3>
              <div className="space-y-3">
                {mockInteractionHistory?.map((interaction) => (
                  <div key={interaction?.id} className="flex items-start space-x-3 p-4 bg-muted/30 rounded-lg">
                    <div className="p-2 rounded-full bg-primary/10">
                      <Icon name={interaction?.icon} size={16} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-text-primary">{interaction?.description}</p>
                      <p className="text-xs text-text-secondary mt-1">{formatDate(interaction?.date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            iconName="Calculator"
            iconPosition="left"
          >
            Nouveau devis
          </Button>
          <Button
            variant="outline"
            iconName="Mail"
            iconPosition="left"
          >
            Envoyer email
          </Button>
          <Button
            variant="default"
            iconName="Edit"
            iconPosition="left"
          >
            Modifier
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClientProfileModal;